import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  KeyRound,
  Loader2,
  Monitor,
  Save,
  ShieldCheck,
} from "lucide-react";

import {
  AuthProvider,
  useAuth,
} from "../context/AuthContext";

import {
  TrainingSessionProvider,
  useTrainingSession,
  TRAINING_ACTIVITY_LABELS,
  formatServiceNumber,
} from "../context/TrainingSessionContext";

import { NoticesGate } from "../components/portal/NoticesGate";
import { Dashboard } from "../components/portal/Dashboard";

import TrainingHome from "./training/TrainingHome";
import NewStarter from "./site/newstarter";
import FirstClass from "./site/firstclass";
import Leading from "./site/leading";

/*
 * ===========================================================================
 * DEVICE ENROLMENT
 * ===========================================================================
 */

function DeviceEnrolment() {
  const {
    enrolDevice,
    authError,
  } = useAuth();

  const [deviceCode, setDeviceCode] =
    useState("");

  const [
    enrolmentKey,
    setEnrolmentKey,
  ] = useState("");

  const [busy, setBusy] =
    useState(false);

  const [localError, setLocalError] =
    useState("");

  const submit = async (event) => {
    event.preventDefault();

    setLocalError("");

    const code =
      deviceCode
        .trim()
        .toUpperCase();

    const key =
      enrolmentKey.trim();

    if (!code) {
      setLocalError(
        "Enter the squadron device code."
      );
      return;
    }

    if (!key) {
      setLocalError(
        "Enter the device enrolment key."
      );
      return;
    }

    setBusy(true);

    try {
      await enrolDevice(
        code,
        key
      );
    } catch (error) {
      setLocalError(
        error?.response?.data
          ?.detail ||
          error?.message ||
          "Device enrolment failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const error =
    localError || authError;

  return (
    <div className="min-h-screen bg-raf-navy flex items-center justify-center p-5">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-raf-navy text-white px-6 py-6 border-b-4 border-raf-red">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-9 h-9" />

              <div>
                <h1 className="text-2xl font-bold">
                  Squadron Device Setup
                </h1>

                <p className="text-white/70 text-sm mt-1">
                  1471 Horwich Squadron
                </p>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="p-6 space-y-5"
          >
            <div>
              <p className="text-slate-700">
                This browser has not yet
                been registered as an
                authorised squadron
                device.
              </p>

              <p className="text-sm text-slate-500 mt-2">
                Device enrolment should
                only be completed by
                authorised staff.
              </p>
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  {error}
                </div>
              </div>
            )}

            <div>
              <label
                htmlFor="deviceCode"
                className="block font-semibold text-slate-800 mb-2"
              >
                Device code
              </label>

              <input
                id="deviceCode"
                value={deviceCode}
                onChange={(event) =>
                  setDeviceCode(
                    event.target.value
                      .toUpperCase()
                  )
                }
                placeholder="e.g. CADET-TABLET-01"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                className="w-full border border-slate-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-raf-navy"
              />
            </div>

            <div>
              <label
                htmlFor="enrolmentKey"
                className="block font-semibold text-slate-800 mb-2"
              >
                Device enrolment key
              </label>

              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />

                <input
                  id="enrolmentKey"
                  type="password"
                  value={enrolmentKey}
                  onChange={(event) =>
                    setEnrolmentKey(
                      event.target.value
                    )
                  }
                  autoComplete="off"
                  className="w-full border border-slate-300 rounded-xl pl-12 pr-4 py-3 outline-none focus:ring-2 focus:ring-raf-navy"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-raf-red text-white font-bold py-3 px-4 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <ShieldCheck className="w-5 h-5" />
                  Register this device
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/*
 * ===========================================================================
 * STAFF PORTAL
 * ===========================================================================
 */

function StaffPortal() {
  const { user } = useAuth();

  const [
    gateCleared,
    setGateCleared,
  ] = useState(false);

  const onCleared =
    useCallback(
      () =>
        setGateCleared(true),
      []
    );

  if (!user) {
    return (
      <LoadingScreen
        text="Opening staff portal..."
      />
    );
  }

  if (!gateCleared) {
    return (
      <NoticesGate
        onCleared={onCleared}
      />
    );
  }

  return <Dashboard />;
}

/*
 * ===========================================================================
 * CADET TRAINING
 * ===========================================================================
 */

function CadetTrainingPortal() {
  const location = useLocation();

  if (
    location.pathname ===
      "/training" ||
    location.pathname ===
      "/training/"
  ) {
    return <TrainingHome />;
  }

  if (
    location.pathname ===
    "/training/newstarter"
  ) {
    return <NewStarter />;
  }

  if (
    location.pathname ===
    "/training/firstclass"
  ) {
    return <FirstClass />;
  }

  if (
    location.pathname ===
    "/training/leading"
  ) {
    return <Leading />;
  }

  return <TrainingHome />;
}

/*
 * ===========================================================================
 * TEMPORARY COURSE PLACEHOLDER
 * ===========================================================================
 */

function CoursePlaceholder({
  activity,
}) {
  const navigate = useNavigate();

  const {
    active,
    serviceNumber,
    activity: activeActivity,
    loading,
    saving,
    ending,
    error,
    endSession,
  } = useTrainingSession();

  useEffect(() => {
    if (loading) {
      return;
    }

    if (
      !active ||
      !serviceNumber
    ) {
      navigate(
        "/training",
        {
          replace: true,
        }
      );
      return;
    }

    if (
      activeActivity &&
      activeActivity !== activity
    ) {
      const paths = {
        newstarter:
          "/training/newstarter",
        firstclass:
          "/training/firstclass",
        leading:
          "/training/leading",
      };

      const correctPath =
        paths[activeActivity];

      if (correctPath) {
        navigate(
          correctPath,
          {
            replace: true,
          }
        );
      }
    }
  }, [
    active,
    activeActivity,
    activity,
    loading,
    navigate,
    serviceNumber,
  ]);

  if (loading) {
    return (
      <LoadingScreen
        text="Loading training..."
      />
    );
  }

  if (
    !active ||
    !serviceNumber
  ) {
    return (
      <LoadingScreen
        text="Returning to training..."
      />
    );
  }

  const title =
    TRAINING_ACTIVITY_LABELS[
      activity
    ] || "Cadet Training";

  const finishSession =
    async () => {
      try {
        await endSession();

        navigate(
          "/training",
          {
            replace: true,
          }
        );
      } catch {
        // Error displayed below.
      }
    };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-raf-navy text-white border-b-4 border-raf-red">
        <div className="max-w-5xl mx-auto px-5 py-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-7 h-7" />
            </div>

            <div>
              <p className="text-white/60 text-xs uppercase tracking-widest font-bold">
                1471 Horwich Squadron
              </p>

              <h1 className="text-2xl font-black">
                {title}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-5 sm:p-8">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500">
            Training record
          </p>

          <p className="mt-1 text-2xl sm:text-3xl font-black text-raf-navy tracking-wide">
            {formatServiceNumber(
              serviceNumber
            )}
          </p>
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-raf-navy" />
          </div>

          <h2 className="mt-5 text-3xl font-black text-raf-navy">
            {title}
          </h2>

          <p className="mt-3 text-slate-600 text-lg">
            Your training record has
            opened successfully.
          </p>

          <div className="mt-6 rounded-xl bg-green-50 border border-green-200 p-4">
            <div className="flex gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-700 shrink-0" />

              <div>
                <p className="font-bold text-green-900">
                  Training record
                  connected
                </p>

                <p className="text-sm text-green-800 mt-1">
                  Progress will be saved
                  against{" "}
                  {formatServiceNumber(
                    serviceNumber
                  )}
                  .
                </p>
              </div>
            </div>
          </div>

          <p className="mt-6 text-slate-500">
            The electronic {title}
            workbook will be added next.
          </p>
        </div>

        {error && (
          <div className="mt-5 rounded-xl bg-red-50 border border-red-200 p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />

              <p className="text-sm text-red-800">
                {error}
              </p>
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            type="button"
            disabled={
              ending || saving
            }
            onClick={
              finishSession
            }
            className="w-full bg-raf-red text-white rounded-xl py-4 px-6 font-black text-lg flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {ending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving session...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                End Session & Save
              </>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}

/*
 * ===========================================================================
 * ATTENDANCE DEVICE
 * ===========================================================================
 */

function AttendancePortal({
  device,
}) {
  return (
    <HoldingScreen
      icon={ClipboardCheck}
      title="Squadron Sign In / Out"
      device={device}
      message="Attendance tablet authorised."
      detail="The dedicated attendance system will be connected separately."
    />
  );
}

/*
 * ===========================================================================
 * DISPLAY DEVICE
 * ===========================================================================
 */

function DisplayPortal({
  device,
}) {
  return (
    <HoldingScreen
      icon={Monitor}
      title="1471 Squadron Display"
      device={device}
      message="Squadron display authorised."
      detail="The read-only parade and training display will be connected separately."
    />
  );
}

/*
 * ===========================================================================
 * GENERIC HOLDING SCREEN
 * ===========================================================================
 */

function HoldingScreen({
  icon: Icon,
  title,
  device,
  message,
  detail,
}) {
  return (
    <div className="min-h-screen bg-raf-navy text-white flex items-center justify-center p-6">
      <div className="max-w-2xl w-full text-center">
        <Icon className="w-20 h-20 mx-auto mb-6" />

        <h1 className="text-4xl font-bold">
          {title}
        </h1>

        <p className="mt-3 text-xl text-white/80">
          {message}
        </p>

        <div className="mt-8 bg-white/10 border border-white/20 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest text-white/50">
            Registered device
          </p>

          <p className="text-2xl font-bold mt-2">
            {device?.label ||
              device?.device_code}
          </p>

          <p className="text-white/60 mt-1">
            {device?.device_code}
          </p>

          <div className="mt-5 inline-flex items-center gap-2 bg-green-500/20 border border-green-300/30 rounded-full px-4 py-2 text-sm">
            <ShieldCheck className="w-4 h-4" />
            Device authorised
          </div>
        </div>

        <p className="mt-7 text-white/60">
          {detail}
        </p>
      </div>
    </div>
  );
}

/*
 * ===========================================================================
 * SERVER UNAVAILABLE
 * ===========================================================================
 */

function ServerUnavailable() {
  const {
    authError,
    resumeDevice,
  } = useAuth();

  const [retrying, setRetrying] =
    useState(false);

  const retry = async () => {
    setRetrying(true);

    try {
      await resumeDevice();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-raf-navy flex items-center justify-center p-5">
      <div className="bg-white max-w-lg w-full rounded-2xl p-6 shadow-2xl">
        <div className="flex gap-3">
          <AlertTriangle className="w-7 h-7 text-amber-500 shrink-0" />

          <div>
            <h1 className="text-xl font-bold text-slate-900">
              Cannot reach the squadron
              server
            </h1>

            <p className="text-slate-600 mt-2">
              {authError}
            </p>

            <p className="text-sm text-slate-500 mt-3">
              This device remains
              enrolled.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={retry}
          disabled={retrying}
          className="mt-6 w-full rounded-xl bg-raf-navy text-white font-bold py-3 flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {retrying && (
            <Loader2 className="w-5 h-5 animate-spin" />
          )}

          Try again
        </button>
      </div>
    </div>
  );
}

/*
 * ===========================================================================
 * LOADING SCREEN
 * ===========================================================================
 */

function LoadingScreen({
  text = "Checking squadron device...",
}) {
  return (
    <div className="min-h-screen flex flex-col gap-4 items-center justify-center bg-raf-navy text-white">
      <Loader2 className="w-9 h-9 animate-spin" />

      <p className="text-white/70">
        {text}
      </p>
    </div>
  );
}

/*
 * ===========================================================================
 * DEVICE ROUTER
 * ===========================================================================
 */

function PortalInner() {
  const {
    device,
    loading,
    authError,
    hasStoredDevice,
  } = useAuth();

  const location =
    useLocation();

  const navigate =
    useNavigate();

  useEffect(() => {
    if (!device) {
      return;
    }

    const path =
      location.pathname;

    switch (device.role) {
      case "staff":
        if (
          !path.startsWith(
            "/staff"
          )
        ) {
          navigate(
            "/staff",
            {
              replace: true,
            }
          );
        }
        break;

      case "attendance_kiosk":
        if (
          !path.startsWith(
            "/attendance"
          )
        ) {
          navigate(
            "/attendance",
            {
              replace: true,
            }
          );
        }
        break;

      case "cadet_training":
        if (
          !path.startsWith(
            "/training"
          )
        ) {
          navigate(
            "/training",
            {
              replace: true,
            }
          );
        }
        break;

      case "display":
        if (
          !path.startsWith(
            "/display"
          )
        ) {
          navigate(
            "/display",
            {
              replace: true,
            }
          );
        }
        break;

      default:
        break;
    }
  }, [
    device,
    location.pathname,
    navigate,
  ]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (
    !device &&
    hasStoredDevice &&
    authError
  ) {
    return (
      <ServerUnavailable />
    );
  }

  if (!device) {
    return (
      <DeviceEnrolment />
    );
  }

  switch (device.role) {
    case "staff":
      return <StaffPortal />;

    case "attendance_kiosk":
      return (
        <AttendancePortal
          device={device}
        />
      );

    case "cadet_training":
      return (
        <TrainingSessionProvider>
          <CadetTrainingPortal />
        </TrainingSessionProvider>
      );

    case "display":
      return (
        <DisplayPortal
          device={device}
        />
      );

    default:
      return (
        <HoldingScreen
          icon={AlertTriangle}
          title="Unknown Device Role"
          device={device}
          message="This device has an unrecognised role."
          detail="Please contact the system administrator."
        />
      );
  }
}

/*
 * ===========================================================================
 * PORTAL
 * ===========================================================================
 */

export default function Portal() {
  return (
    <AuthProvider>
      <PortalInner />
    </AuthProvider>
  );
}